import React from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Check, Sparkles, Zap, Shield } from 'lucide-react';
import { memberships } from '@/data/memberships';
import { useAuth } from '@/contexts/MyContext';
import { useToast } from '@/hooks/use-toast';

const PricingTable: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const handlePurchase = (id: string) => {
    navigate(`/purchase/${id}`);
    localStorage.setItem('intendedPurchase', id);
  };

  const renderPopularBadge = () => (
    <div className="absolute -top-3 right-10">
      <span className="inline-flex rounded-full bg-gradient-to-r from-orange-400 to-orange-500 px-3 py-1 text-xs font-semibold text-white">
        Most Popular
      </span>
    </div>
  );

  const mainProducts = memberships.filter(item => 
    item.id === 'launch-guide' || item.id === 'fast-track'
  );

  const advancedProducts = memberships.filter(item => 
    item.id === 'accelerator' || item.id === 'business-mastery'
  );

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid gap-8 lg:grid-cols-2 md:grid-cols-2 sm:grid-cols-1">
        {mainProducts.map((plan) => (
          <div
            key={plan.id}
            className={`relative flex flex-col rounded-xl border p-6 shadow-md transition-transform hover:shadow-xl ${
              plan.popular ? 'border-orange-400 ring-2 ring-orange-400 transform hover:-translate-y-2' : 'border-gray-200 hover:-translate-y-1'
            }`}
          >
            {plan.popular && renderPopularBadge()}
            
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-1">
                {plan.popular ? 
                  <Sparkles className="h-5 w-5 text-orange-500" /> : 
                  <Zap className="h-5 w-5 text-blue-500" />
                }
                <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
              </div>
              
              <div className="mt-4 flex items-baseline">
                <span className="text-3xl font-extrabold tracking-tight text-gray-900">${plan.price}</span>
                {plan.id === 'unlimited' ? 
                  <span className="ml-1 text-lg text-gray-500">/month</span> : 
                  <span className="ml-1 text-lg text-gray-500">/one-time</span>
                }
              </div>
              
              {plan.productTitle && (
                <p className="mt-2 text-sm font-medium text-orange-600 italic">"{plan.productTitle}"</p>
              )}
              
              <p className="mt-3 text-sm text-gray-500">{plan.description}</p>
            </div>
            
            <div className="border-t border-gray-100 pt-4 mb-6 flex-1">
              <ul className="mt-4 space-y-3">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start">
                    <Check className="h-5 w-5 shrink-0 text-green-500 mt-0.5" />
                    <span className="ml-2 text-sm text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <Button 
              className={`w-full ${plan.popular ? "bg-orange-500 hover:bg-orange-600 text-white" : ""}`}
              variant={plan.popular ? "default" : "outline"}
              onClick={() => handlePurchase(plan.id)}
            >
              {plan.callToAction}
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-12 grid gap-8 md:grid-cols-2">
        {advancedProducts.map((plan) => (
          <div
            key={plan.id}
            className="relative flex flex-col rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 p-6 shadow-lg transition-transform hover:-translate-y-1 hover:shadow-xl"
          >
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="h-5 w-5 text-blue-600" />
                <h3 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">{plan.name}</h3>
              </div>
              
              <div className="mt-4 flex items-baseline">
                <span className="text-3xl font-extrabold tracking-tight">${plan.price}</span>
                {plan.id === 'unlimited' ? 
                  <span className="ml-1 text-lg text-gray-600">/month</span> : 
                  <span className="ml-1 text-lg text-gray-600">/one-time</span>
                }
              </div>
              
              {plan.productTitle && (
                <p className="mt-2 text-sm font-medium text-blue-600 italic">"{plan.productTitle}"</p>
              )}
              
              <p className="mt-3 text-sm text-gray-600">{plan.description}</p>
            </div>
            
            <div className="border-t border-blue-100 pt-4 mb-6 flex-1">
              <div className="mt-4 grid grid-cols-2 gap-4">
                <ul className="space-y-3">
                  {plan.features.slice(0, Math.ceil(plan.features.length / 2)).map((feature) => (
                    <li key={feature} className="flex items-start">
                      <Check className="h-5 w-5 shrink-0 text-green-500 mt-0.5" />
                      <span className="ml-2 text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                <ul className="space-y-3">
                  {plan.features.slice(Math.ceil(plan.features.length / 2)).map((feature) => (
                    <li key={feature} className="flex items-start">
                      <Check className="h-5 w-5 shrink-0 text-green-500 mt-0.5" />
                      <span className="ml-2 text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium"
              onClick={() => handlePurchase(plan.id)}
            >
              {plan.callToAction}
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-16 grid gap-12">
        <div className="relative rounded-2xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 opacity-90"></div>
          <div className="absolute inset-0 bg-[url(/placeholder.svg)] mix-blend-overlay opacity-10"></div>
          <div className="relative p-8 text-white">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
              <div className="md:max-w-md">
                <h3 className="text-2xl font-bold flex items-center gap-2">
                  <Zap className="h-6 w-6" /> 
                  {memberships[5].name}
                </h3>
                <div className="mt-4 flex items-baseline">
                  <span className="text-5xl font-extrabold tracking-tight">${memberships[5].price}</span>
                  <span className="ml-1 text-2xl">/month</span>
                </div>
                <p className="mt-1 text-sm opacity-80 text-left">No contract, cancel anytime.</p>
                <p className="mt-4 text-lg opacity-90 text-left">
                  Unlimited AI App Development for One Monthly Price
                </p>
                <p className="mt-2 text-white/80 text-left">
                  Get unlimited AI app builds, updates, and expert support—no extra fees, no limits
                </p>
              </div>
              
              <div className="md:flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                    <Check className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Unlimited app builds</span>
                  </div>
                  <div className="flex items-start gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                    <Check className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Ongoing updates & improvements</span>
                  </div>
                  <div className="flex items-start gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                    <Check className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Priority support access</span>
                  </div>
                  <div className="flex items-start gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                    <Check className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Monthly strategy sessions</span>
                  </div>
                  <div className="flex items-start gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                    <Check className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Custom feature development</span>
                  </div>
                  <div className="flex items-start gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                    <Check className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Publishing assistance</span>
                  </div>
                </div>
              </div>
              
              <div className="flex-shrink-0 mt-6 md:mt-0">
                <Button 
                  className="w-full md:w-auto px-8 py-6 text-lg font-medium bg-white text-blue-600 hover:bg-blue-50"
                  onClick={() => handlePurchase(memberships[5].id)}
                >
                  {memberships[5].callToAction}
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="relative rounded-2xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-90"></div>
          <div className="absolute inset-0 bg-[url(/placeholder.svg)] mix-blend-overlay opacity-10"></div>
          <div className="relative p-8 text-white">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
              <div className="md:max-w-md">
                <h3 className="text-2xl font-bold flex items-center gap-2">
                  <Shield className="h-6 w-6" /> 
                  {memberships[6].name}
                </h3>
                <div className="mt-4 flex items-baseline">
                  <span className="text-5xl font-extrabold tracking-tight">${memberships[6].price.toLocaleString()}</span>
                  <span className="ml-1 text-2xl">/one-time</span>
                </div>
                <p className="mt-4 text-lg opacity-90 text-left">
                  We Build & Launch Your AI App for You—From Idea to Market
                </p>
                <p className="mt-2 text-white/80 text-left">
                  A done-for-you AI app development service with custom branding, monetization, and launch strategy built in
                </p>
              </div>
              
              <div className="md:flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                    <Check className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Custom app development</span>
                  </div>
                  <div className="flex items-start gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                    <Check className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Professional UI/UX design</span>
                  </div>
                  <div className="flex items-start gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                    <Check className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Branding & identity creation</span>
                  </div>
                  <div className="flex items-start gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                    <Check className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Monetization strategy</span>
                  </div>
                  <div className="flex items-start gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                    <Check className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Launch campaign</span>
                  </div>
                  <div className="flex items-start gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                    <Check className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Marketing materials</span>
                  </div>
                  <div className="flex items-start gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                    <Check className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">App store optimization</span>
                  </div>
                  <div className="flex items-start gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                    <Check className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">VIP support</span>
                  </div>
                </div>
              </div>
              
              <div className="flex-shrink-0 mt-6 md:mt-0">
                <Button 
                  className="w-full md:w-auto px-8 py-6 text-lg font-medium bg-white text-purple-600 hover:bg-purple-50"
                  onClick={() => handlePurchase(memberships[6].id)}
                >
                  {memberships[6].callToAction}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingTable;
